import dayjs from 'dayjs';

import { ColDef } from '~components/dataGrids/interfaces/colDef';
import { GridColTypeEnum } from '~components/dataGrids/interfaces/gridColTypeEnum';
import { UserRoleEnum } from '~enums';
import { GeoCoordinate } from '~interfaces';
import i18n from '~lib/i18n';

interface DeviceLog {
	avlUndefined: string | undefined;
	canBus1: string | undefined;
	canBus2: string | undefined;
	canBus3: string | undefined;
	fuelLevelPercentage: number | undefined;
	canProgram: number | undefined;
	canSecurityStateFlags: string | undefined;
	batteryRangeMeters: number | undefined;
	speedKiloMeters: number | undefined;
	clientId: number | undefined;
	codecId: string | undefined;
	deviceId: number | undefined;
	imei: string | undefined;
	locationAltitude: number | undefined;
	locationAngle: number | undefined;
	locationLatitude: number | undefined;
	locationLongitude: number | undefined;
	locationSatellitesUsed: number | undefined;
	locationSpeed: number | undefined;
	lockState: string | undefined;
	deviceTimestamp: string | undefined;
	eventCode: string | undefined;
	id: string | undefined;
	ioBatteryPercentage: number | undefined;
	ioBluetoothStatus: string | undefined;
	ioDigitalIn1: boolean | undefined;
	ioDigitalIn2: boolean | undefined;
	ioDigitalOut1: boolean | undefined;
	ioExternalVoltage: number | undefined;
	ioFuelConsumerLiters: number | undefined;
	locationGnssHdop: number | undefined;
	locationGnssPdop: number | undefined;
	locationGnssStatus: string | undefined;
	locationGmsActiveOperator: number | undefined;
	locationGsmSignalAreaCode: number | undefined;
	locationGsmSignalStrength: string | undefined;
	ioIbuttonCode: string | undefined;
	ioIccid: string | undefined;
	isIgnitionOn: boolean | undefined;
	isMoving: boolean | undefined;
	ioPropertyCount: number | undefined;
	ioRfidTag: number | undefined;
	ioSleepMode: string | undefined;
	ioSpeedKiloMetersPerHour: number | undefined;
	ioMileageCountedInMeters: number | undefined;
	ioMileageInMeters: number | undefined;
	ioOdoMeterTotal: number | undefined;
	ioTripOdoMeter: number | undefined;
	ioTripStatus: string | undefined;
	ioUmtsLteId: number | undefined;
	priority: string | undefined;
	serverTimestamp: string | undefined;
	batteryPercentage: number | undefined;
	chargerState: string | undefined;
	configConnectivityProfile: number | undefined;
	configNfcSensitivity: number | undefined;
	configNfcTuning: string | undefined;
	debug: string | undefined;
	firmwareVersion: string | undefined;
	hardwareId: string | undefined;
	locationResult: string | undefined;
	locationSatellitesSeen: number | undefined;
	locationTtf: number | undefined;
	lockAssetFirmwareVersion: string | undefined;
	lockAssetId: string | undefined;
	modemCellId: number | undefined;
	modemCellLac: number | undefined;
	modemCellMcc: number | undefined;
	modemCellMnc: number | undefined;
	modemCellRsrp: number | undefined;
	modemCoprocessorFirmwareVersion: string | undefined;
	modemFirmwareVersion: string | undefined;
	modemImei: string | undefined;
	modemSimIccid: string | undefined;
	modemSimImsi: string | undefined;
	configOfflineKeyCount: number | undefined;
	rssi: number | undefined;
	deviceState: string | undefined;
	statsTotalReboots: number | undefined;
	statsTotalUnlocks: number | undefined;
	statusCode: number | undefined;
	temperature: number | undefined;
	wheelTurns: number | undefined;
	eventType: string | undefined;
	keySerialNumber: string | undefined;
	keyTagType: string | undefined;
	keyTokenId: string | undefined;
	proxyId: string | undefined;
	proxyTimeZone: string | undefined;
	proxyType: string | undefined;
	timeStamp: string | undefined;
	traceId: string | undefined;
	accessCorrelationId: string | undefined;
	accessKeyId: number | undefined;
	accessRuleId: number | undefined;
	accessState: string | undefined;
	accessType: string | undefined;
	statsTotalAccessCalls: number | undefined;
	statsTotalAccessError: number | undefined;
	statsTotalAccessFailed: number | undefined;
	statsTotalAccessSuccess: number | undefined;
	statsTotalReconnects: number | undefined;
	borderRouterHostName: string | undefined;
	borderRouterIfMode: number | undefined;
	borderRouterIpAddress: string | undefined;
	borderRouterThreadMode: number | undefined;
	borderRouterTxPower: number | undefined;
}

type deviceLogColumns =
	// Exceptions
	| 'immobiliser' // is same as digitalOut1, but we'll get questions about it.
	| 'location' // Combination of latitude and longitude.
	| 'chargingStatus' // Based on ioExternalVoltage > 0
	// 1:1 with backend (removed io. camelCase the rest.)
	| 'avlUndefined'
	| 'canBus1'
	| 'canBus2'
	| 'canBus3'
	| 'fuelLevelPercentage'
	| 'canProgram'
	| 'canSecurityStateFlags'
	| 'batteryRangeMeters'
	| 'speedKiloMeters'
	| 'clientId'
	| 'codecId'
	| 'deviceId'
	| 'imei'
	| 'locationAltitude'
	| 'locationAngle'
	| 'locationSatellitesUsed'
	| 'locationSpeed'
	| 'lockState'
	| 'deviceTimestamp'
	| 'eventCode'
	| 'id'
	| 'ioBatteryPercentage'
	| 'ioBluetoothStatus'
	| 'ioDigitalIn1'
	| 'ioDigitalIn2'
	| 'ioDigitalOut1'
	| 'ioExternalVoltage'
	| 'ioFuelConsumerLiters'
	| 'locationGnssHdop'
	| 'locationGnssPdop'
	| 'locationGnssStatus'
	| 'locationGmsActiveOperator'
	| 'locationGsmSignalAreaCode'
	| 'locationGsmSignalStrength'
	| 'ioIbuttonCode'
	| 'ioIccid'
	| 'isIgnitionOn'
	| 'isMoving'
	| 'ioPropertyCount'
	| 'ioRfidTag'
	| 'ioSleepMode'
	| 'ioSpeedKiloMetersPerHour'
	| 'ioMileageCountedInMeters'
	| 'ioMileageInMeters'
	| 'ioOdoMeterTotal'
	| 'ioTripOdoMeter'
	| 'ioTripStatus'
	| 'ioUmtsLteId'
	| 'priority'
	| 'serverTimestamp'
	| 'batteryPercentage'
	| 'chargerState'
	| 'configConnectivityProfile'
	| 'configNfcSensitivity'
	| 'configNfcTuning'
	| 'debug'
	| 'firmwareVersion'
	| 'hardwareId'
	| 'locationResult'
	| 'locationSatellitesSeen'
	| 'locationTtf'
	| 'lockAssetFirmwareVersion'
	| 'lockAssetId'
	| 'modemCellId'
	| 'modemCellLac'
	| 'modemCellMcc'
	| 'modemCellMnc'
	| 'modemCellRsrp'
	| 'modemCoprocessorFirmwareVersion'
	| 'modemFirmwareVersion'
	| 'modemImei'
	| 'modemSimIccid'
	| 'modemSimImsi'
	| 'configOfflineKeyCount'
	| 'rssi'
	| 'deviceState'
	| 'statsTotalReboots'
	| 'statsTotalUnlocks'
	| 'statusCode'
	| 'temperature'
	| 'wheelTurns'
	| 'eventType'
	| 'keySerialNumber'
	| 'keyTagType'
	| 'keyTokenId'
	| 'proxyId'
	| 'proxyTimeZone'
	| 'proxyType'
	| 'timeStamp'
	| 'traceId'
	| 'accessCorrelationId'
	| 'accessKeyId'
	| 'accessRuleId'
	| 'accessState'
	| 'accessType'
	| 'statsTotalAccessCalls'
	| 'statsTotalAccessError'
	| 'statsTotalAccessFailed'
	| 'statsTotalAccessSuccess'
	| 'statsTotalReconnects'
	| 'borderRouterHostName'
	| 'borderRouterIfMode'
	| 'borderRouterIpAddress'
	| 'borderRouterThreadMode'
	| 'borderRouterTxPower';

export const deviceLogGridCols: Record<deviceLogColumns, ColDef<DeviceLog>> = {
	serverTimestamp: {
		field: 'serverTimestamp',
		headerName: 'entities:device.columns.serverTimestamp',
		defaultShown: false,
		gridColType: GridColTypeEnum.Dayjs,
		valueGetter: (value: DeviceLog) => dayjs(value.serverTimestamp),
	},
	location: {
		field: 'location',
		headerName: 'entities:device.columns.location',
		defaultShown: true,
		gridColType: GridColTypeEnum.Location,
		valueGetter: (value: DeviceLog) =>
			value.locationLatitude && value.locationLongitude ?
				({ latitude: value.locationLatitude, longitude: value.locationLongitude } as GeoCoordinate)
			:	'',
	},
	deviceState: {
		field: 'deviceState',
		headerName: 'entities:device.columns.deviceState',
		defaultShown: true,
	},
	lockState: {
		field: 'lockState',
		headerName: 'entities:device.columns.lockState',
		defaultShown: true,
	},
	firmwareVersion: {
		field: 'firmwareVersion',
		headerName: 'entities:device.columns.firmwareVersion',
		defaultShown: true,
	},
	isIgnitionOn: {
		field: 'isIgnitionOn',
		headerName: 'entities:device.columns.isIgnitionOn',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			i18n.t(value.isIgnitionOn ? 'enabled' : 'disabled', { ns: 'status' }),
	},
	immobiliser: {
		field: 'immobiliser',
		headerName: 'entities:device.columns.immobiliser',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			i18n.t(!value.ioDigitalOut1 ? 'enabled' : 'disabled', { ns: 'status' }),
	},
	ioIbuttonCode: {
		field: 'keyDetected',
		headerName: 'entities:device.columns.ioIbuttonCode',
		defaultShown: true,
		maxWidth: 100,
	},
	speedKiloMeters: {
		field: 'speedKiloMeters',
		headerName: 'entities:device.columns.speedKiloMeters',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			i18n.t('kmPerHour', {
				ns: 'system',
				count: value.speedKiloMeters ?? 0,
			}),
	},
	ioOdoMeterTotal: {
		field: 'ioOdoMeterTotal',
		headerName: 'entities:device.columns.ioOdoMeterTotal',
		defaultShown: true,
		valueGetter: (value: DeviceLog) => {
			const totalMileage = value.ioOdoMeterTotal ?? 0;
			const totalMileageKm = totalMileage / 1000;
			const formattedTotalMileageKm =
				Number.isInteger(totalMileageKm) ? `${totalMileageKm}` : `${totalMileageKm.toFixed(0)}`;
			return `${formattedTotalMileageKm} ${i18n.t('kmShort', { ns: 'system' })}`;
		},
	},
	batteryPercentage: {
		field: 'batteryPercentage',
		headerName: 'entities:device.columns.batteryPercentage',
		valueGetter: (value: DeviceLog) => `${value.batteryPercentage}%`,
		defaultShown: true,
		minWidth: 80,
		maxWidth: 80,
	},
	chargingStatus: {
		field: 'chargingStatus',
		headerName: 'entities:device.columns.chargingStatus',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			i18n.t((value.ioExternalVoltage ?? 0) > 0 ? 'charging' : 'notCharging', { ns: 'status' }),
	},
	batteryRangeMeters: {
		field: 'batteryRangeMeters',
		headerName: 'entities:device.columns.batteryRangeMeters',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			value.batteryRangeMeters !== undefined ?
				`${value.batteryRangeMeters}${i18n.t('kmShort', { ns: 'system' })}`
			:	'',
	},
	temperature: {
		field: 'temperature',
		headerName: 'entities:device.columns.temperature',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			value.temperature ? `${value.temperature?.toFixed(1)}°C` : '',
		maxWidth: 100,
		minWidth: 100,
	},
	rssi: {
		field: 'rssi',
		headerName: 'entities:device.columns.rssi',
		defaultShown: true,
		valueGetter: (value: DeviceLog) => (value.rssi !== undefined ? `${value.rssi} dBm` : ''),
	},
	eventCode: {
		field: 'eventCode',
		headerName: 'entities:device.columns.eventCode',
		defaultShown: false,
	},
	configNfcSensitivity: {
		field: 'configNfcSensitivity',
		headerName: 'entities:device.columns.configNfcSensitivity',
		defaultShown: true,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	eventType: {
		field: 'eventType',
		headerName: 'entities:device.labels.eventType',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			(
				value.eventType?.toLowerCase() === 'unspecified' ||
				value.eventType?.toLowerCase() === 'none'
			) ?
				''
			:	value.eventType,
	},
	locationSatellitesSeen: {
		field: 'satellitesSeen',
		headerName: 'entities:device.labels.satellitesSeen',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			i18n.t('sattelite.labels.count', {
				ns: 'entities',
				count: value.locationSatellitesSeen ?? 0,
			}),
	},
	locationSatellitesUsed: {
		field: 'satellitesUsed',
		headerName: 'entities:device.labels.satellitesUsed',
		defaultShown: true,
		valueGetter: (value: DeviceLog) =>
			i18n.t('sattelite.labels.count', {
				ns: 'entities',
				count: value.locationSatellitesUsed ?? 0,
			}),
	},
	traceId: {
		field: 'traceId',
		headerName: 'entities:device.columns.traceId',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	avlUndefined: {
		field: 'avlUndefined',
		headerName: 'entities:device.columns.avlUndefined',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	canBus1: {
		field: 'canBus1',
		headerName: 'entities:device.columns.canBus1',
		defaultShown: false,
	},
	canBus2: {
		field: 'canBus2',
		headerName: 'entities:device.columns.canBus2',
		defaultShown: false,
	},
	canBus3: {
		field: 'canBus3',
		headerName: 'entities:device.columns.canBus3',
		defaultShown: false,
	},
	fuelLevelPercentage: {
		field: 'fuelLevelPercentage',
		headerName: 'entities:device.columns.fuelLevelPercentage',
		defaultShown: false,
	},
	canProgram: {
		field: 'canProgram',
		headerName: 'entities:device.columns.canProgram',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	canSecurityStateFlags: {
		field: 'canSecurityStateFlags',
		headerName: 'entities:device.columns.canSecurityStateFlags',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	locationAltitude: {
		field: 'locationAltitude',
		headerName: 'entities:device.columns.locationAltitude',
		defaultShown: false,
	},
	deviceTimestamp: {
		field: 'deviceTimestamp',
		headerName: 'entities:device.columns.deviceTimestamp',
		defaultShown: true,
		gridColType: GridColTypeEnum.Dayjs,
		valueGetter: (value: DeviceLog) => dayjs(value.deviceTimestamp),
	},
	ioBluetoothStatus: {
		field: 'ioBluetoothStatus',
		headerName: 'entities:device.columns.ioBluetoothStatus',
		defaultShown: false,
	},
	ioDigitalIn1: {
		field: 'ioDigitalIn1',
		headerName: 'entities:device.columns.ioDigitalIn1',
		defaultShown: true,
	},
	ioDigitalIn2: {
		field: 'ioDigitalIn2',
		headerName: 'entities:device.columns.ioDigitalIn2',
		defaultShown: false,
	},
	ioDigitalOut1: {
		field: 'ioDigitalOut1',
		headerName: 'entities:device.columns.ioDigitalOut1',
		defaultShown: false,
	},
	ioExternalVoltage: {
		field: 'ioExternalVoltage',
		headerName: 'entities:device.columns.ioExternalVoltage',
		defaultShown: false,
	},
	ioFuelConsumerLiters: {
		field: 'ioFuelConsumerLiters',
		headerName: 'entities:device.columns.ioFuelConsumerLiters',
		defaultShown: false,
	},
	locationGnssHdop: {
		field: 'locationGnssHdop',
		headerName: 'entities:device.columns.locationGnssHdop',
		defaultShown: false,
	},
	locationGnssPdop: {
		field: 'locationGnssPdop',
		headerName: 'entities:device.columns.locationGnssPdop',
		defaultShown: false,
	},
	locationGnssStatus: {
		field: 'locationGnssStatus',
		headerName: 'entities:device.columns.locationGnssStatus',
		defaultShown: false,
	},
	locationGmsActiveOperator: {
		field: 'locationGmsActiveOperator',
		headerName: 'entities:device.columns.locationGmsActiveOperator',
		defaultShown: false,
	},
	locationGsmSignalAreaCode: {
		field: 'locationGsmSignalAreaCode',
		headerName: 'entities:device.columns.locationGsmSignalAreaCode',
		defaultShown: false,
	},
	locationGsmSignalStrength: {
		field: 'locationGsmSignalStrength',
		headerName: 'entities:device.columns.locationGsmSignalStrength',
		defaultShown: false,
	},
	ioIccid: {
		field: 'ioIccid',
		headerName: 'entities:device.columns.ioIccid',
		defaultShown: false,
	},
	ioPropertyCount: {
		field: 'ioPropertyCount',
		headerName: 'entities:device.columns.ioPropertyCount',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	ioRfidTag: {
		field: 'ioRfidTag',
		headerName: 'entities:device.columns.ioRfidTag',
		defaultShown: false,
	},
	ioSleepMode: {
		field: 'ioSleepMode',
		headerName: 'entities:device.columns.ioSleepMode',
		defaultShown: false,
	},
	ioSpeedKiloMetersPerHour: {
		field: 'ioSpeedKiloMetersPerHour',
		headerName: 'entities:device.columns.ioSpeedKiloMetersPerHour',
		defaultShown: true,
	},
	ioMileageCountedInMeters: {
		field: 'ioMileageCountedInMeters',
		headerName: 'entities:device.columns.ioMileageCountedInMeters',
		defaultShown: false,
	},
	ioMileageInMeters: {
		field: 'ioMileageInMeters',
		headerName: 'entities:device.columns.ioMileageInMeters',
		defaultShown: true,
	},
	ioTripOdoMeter: {
		field: 'ioTripOdoMeter',
		headerName: 'entities:device.columns.ioTripOdoMeter',
		defaultShown: false,
	},
	ioUmtsLteId: {
		field: 'ioUmtsLteId',
		headerName: 'entities:device.columns.ioUmtsLteId',
		defaultShown: false,
	},
	clientId: {
		field: 'clientId',
		headerName: 'entities:device.columns.clientId',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	codecId: {
		field: 'codecId',
		headerName: 'entities:device.columns.codecId',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	deviceId: {
		field: 'deviceId',
		headerName: 'entities:device.columns.deviceId',
		defaultShown: false,
	},
	imei: {
		field: 'imei',
		headerName: 'entities:device.columns.imei',
		defaultShown: false,
	},
	locationAngle: {
		field: 'locationAngle',
		headerName: 'entities:device.columns.locationAngle',
		defaultShown: false,
	},
	locationSpeed: {
		field: 'locationSpeed',
		headerName: 'entities:device.columns.locationSpeed',
		defaultShown: false,
	},
	id: {
		field: 'id',
		headerName: 'entities:device.columns.id',
		defaultShown: false,
	},
	ioBatteryPercentage: {
		field: 'ioBatteryPercentage',
		headerName: 'entities:device.columns.ioBatteryPercentage',
		defaultShown: true,
	},
	isMoving: {
		field: 'isMoving',
		headerName: 'entities:device.columns.isMoving',
		defaultShown: false,
	},
	ioTripStatus: {
		field: 'ioTripStatus',
		headerName: 'entities:device.columns.ioTripStatus',
		defaultShown: false,
	},
	priority: {
		field: 'priority',
		headerName: 'entities:device.columns.priority',
		defaultShown: false,
	},
	chargerState: {
		field: 'chargerState',
		headerName: 'entities:device.columns.chargerState',
		defaultShown: false,
	},
	configConnectivityProfile: {
		field: 'configConnectivityProfile',
		headerName: 'entities:device.columns.configConnectivityProfile',
		defaultShown: false,
	},
	configNfcTuning: {
		field: 'configNfcTuning',
		headerName: 'entities:device.columns.configNfcTuning',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	debug: {
		field: 'debug',
		headerName: 'entities:device.columns.debug',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	hardwareId: {
		field: 'hardwareId',
		headerName: 'entities:device.columns.hardwareId',
		defaultShown: false,
	},
	locationResult: {
		field: 'locationResult',
		headerName: 'entities:device.columns.locationResult',
		defaultShown: false,
	},
	locationTtf: {
		field: 'locationTtf',
		headerName: 'entities:device.columns.locationTtf',
		defaultShown: false,
	},
	lockAssetFirmwareVersion: {
		field: 'lockAssetFirmwareVersion',
		headerName: 'entities:device.columns.lockAssetFirmwareVersion',
		defaultShown: true,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	lockAssetId: {
		field: 'lockAssetId',
		headerName: 'entities:device.columns.lockAssetId',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemCellId: {
		field: 'modemCellId',
		headerName: 'entities:device.columns.modemCellId',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemCellLac: {
		field: 'modemCellLac',
		headerName: 'entities:device.columns.modemCellLac',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemCellMcc: {
		field: 'modemCellMcc',
		headerName: 'entities:device.columns.modemCellMcc',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemCellMnc: {
		field: 'modemCellMnc',
		headerName: 'entities:device.columns.modemCellMnc',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemCellRsrp: {
		field: 'modemCellRsrp',
		headerName: 'entities:device.columns.modemCellRsrp',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemCoprocessorFirmwareVersion: {
		field: 'modemCoprocessorFirmwareVersion',
		headerName: 'entities:device.columns.modemCoprocessorFirmwareVersion',
		defaultShown: false,
	},
	modemFirmwareVersion: {
		field: 'modemFirmwareVersion',
		headerName: 'entities:device.columns.modemFirmwareVersion',
		defaultShown: false,
	},
	modemImei: {
		field: 'modemImei',
		headerName: 'entities:device.columns.modemImei',
		defaultShown: false,
	},
	modemSimIccid: {
		field: 'modemSimIccid',
		headerName: 'entities:device.columns.modemSimIccid',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	modemSimImsi: {
		field: 'modemSimImsi',
		headerName: 'entities:device.columns.modemSimImsi',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	configOfflineKeyCount: {
		field: 'configOfflineKeyCount',
		headerName: 'entities:device.columns.configOfflineKeyCount',
		defaultShown: false,
	},
	statsTotalReboots: {
		field: 'statsTotalReboots',
		headerName: 'entities:device.columns.statsTotalReboots',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	statsTotalUnlocks: {
		field: 'statsTotalUnlocks',
		headerName: 'entities:device.columns.statsTotalUnlocks',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	statusCode: {
		field: 'statusCode',
		headerName: 'entities:device.columns.statusCode',
		defaultShown: false,
	},
	wheelTurns: {
		field: 'wheelTurns',
		headerName: 'entities:device.columns.wheelTurns',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	keySerialNumber: {
		field: 'keySerialNumber',
		headerName: 'entities:device.columns.keySerialNumber',
		defaultShown: false,
	},
	keyTagType: {
		field: 'keyTagType',
		headerName: 'entities:device.columns.keyTagType',
		defaultShown: false,
	},
	keyTokenId: {
		field: 'keyTokenId',
		headerName: 'entities:device.columns.keyTokenId',
		defaultShown: true,
	},
	proxyId: {
		field: 'proxyId',
		headerName: 'entities:device.columns.proxyId',
		defaultShown: false,
	},
	proxyTimeZone: {
		field: 'proxyTimeZone',
		headerName: 'entities:device.columns.proxyTimeZone',
		defaultShown: false,
	},
	proxyType: {
		field: 'proxyType',
		headerName: 'entities:device.columns.proxyType',
		defaultShown: false,
	},
	timeStamp: {
		field: 'timeStamp',
		headerName: 'entities:device.columns.timeStamp',
		defaultShown: true,
		gridColType: GridColTypeEnum.Dayjs,
		valueGetter: (value: DeviceLog) => dayjs(value.timeStamp),
	},
	accessCorrelationId: {
		field: 'accessCorrelationId',
		headerName: 'entities:device.columns.accessCorrelationId',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	accessKeyId: {
		field: 'accessKeyId',
		headerName: 'entities:device.columns.accessKeyId',
		defaultShown: false,
	},
	accessRuleId: {
		field: 'accessRuleId',
		headerName: 'entities:device.columns.accessRuleId',
		defaultShown: false,
	},
	accessState: {
		field: 'accessState',
		headerName: 'entities:device.columns.accessState',
		defaultShown: false,
	},
	accessType: {
		field: 'accessType',
		headerName: 'entities:device.columns.accessType',
		defaultShown: false,
	},
	statsTotalAccessCalls: {
		field: 'statsTotalAccessCalls',
		headerName: 'entities:device.columns.statsTotalAccessCalls',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	statsTotalAccessError: {
		field: 'statsTotalAccessError',
		headerName: 'entities:device.columns.statsTotalAccessError',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	statsTotalAccessFailed: {
		field: 'statsTotalAccessFailed',
		headerName: 'entities:device.columns.statsTotalAccessFailed',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	statsTotalAccessSuccess: {
		field: 'statsTotalAccessSuccess',
		headerName: 'entities:device.columns.statsTotalAccessSuccess',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	statsTotalReconnects: {
		field: 'statsTotalReconnects',
		headerName: 'entities:device.columns.statsTotalReconnects',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	borderRouterHostName: {
		field: 'borderRouterHostName',
		headerName: 'entities:device.columns.borderRouterHostName',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	borderRouterIfMode: {
		field: 'borderRouterIfMode',
		headerName: 'entities:device.columns.borderRouterIfMode',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	borderRouterIpAddress: {
		field: 'borderRouterIpAddress',
		headerName: 'entities:device.columns.borderRouterIpAddress',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	borderRouterThreadMode: {
		field: 'borderRouterThreadMode',
		headerName: 'entities:device.columns.borderRouterThreadMode',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
	borderRouterTxPower: {
		field: 'borderRouterTxPower',
		headerName: 'entities:device.columns.borderRouterTxPower',
		defaultShown: false,
		roleFilters: [UserRoleEnum.SuperAdmin, UserRoleEnum.Support],
	},
};
export default DeviceLog;
